import { IJournalImportResult } from './journal-import.model';

/**
 * Proffix journal import wire shape (POST /api/integrations/proffix/import).
 *
 * The RESULT shape is shared with D365 BC / Abacus / Topal via `IJournalImportResult`
 * (`./journal-import.model`). Proffix has no fiscal-year list endpoint: the FE pulls
 * `fiscalYear*` (and optional `previousFiscalYear*`) from the active Annual Report's
 * General Info, and the BE uses the dates directly as journal filter bounds (Abacus pattern).
 */
export interface IProffixImportBody {
    companyId: string;
    connectionId: string;
    /** Proffix database (Mandant) name, e.g. `DEMODB`, sourced from the connection mapping. */
    externalCompanyId: string;
    /** ISO YYYY-MM-DD. */
    fiscalYearStart: string;
    fiscalYearEnd: string;
    /** Optional YoY pair — both halves or neither (the BE rejects a partial pair). */
    previousFiscalYearStart?: string;
    previousFiscalYearEnd?: string;
}

/**
 * The report a Proffix import was started for. Kept in the store next to the run, never sent to the BE.
 * The import step uses it to take a result only for the report on screen (frontend guidelines §4:
 * late results check where the user is now).
 */
export interface IProffixImportTarget {
    companyId: string;
    annualReportId: string;
}

/**
 * The result of the Proffix import slice, but only when it succeeded FOR this report.
 *
 * The slice is global and the import effect outlives the import step, so a success can land (or still sit
 * in the store) after the user opened another report. That report gets `null`, as does a success whose
 * target was cleared by a reset while the run was in flight.
 */
export function proffixImportResultForReport(
    slice: {
        importStatus: 'idle' | 'loading' | 'success' | 'error';
        importResult: IJournalImportResult | null;
        importFor: IProffixImportTarget | null;
    },
    report: { companyId: string | null | undefined; annualReportId: string | null | undefined },
): IJournalImportResult | null {
    if (slice.importStatus !== 'success' || slice.importResult == null || slice.importFor == null) return null;
    if (report.companyId == null || report.annualReportId == null) return null;
    const matches =
        slice.importFor.companyId === report.companyId && slice.importFor.annualReportId === report.annualReportId;
    return matches ? slice.importResult : null;
}
